/* eslint-disable complexity */
import {
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  faCheck,
  faCircleDot,
  faEllipsisH,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import {
  CommentDeepFragment,
  ElementIdentifierDbidType,
  GetOrderNameDocument,
  GetProjectNameDocument,
  SavedViewDetailsFragment,
  UpdateCommentMutationDocument,
} from '../../gql/graphql';
import { useUserTenant } from '../../services/auth-info';
import { ago } from '../../utils/time-utils';
import CircleIcon from '../common/CircleIcon';
import ConfirmDeletionModal from '../common/ConfirmDeletionModal';
import SparkelIcon from '../common/icon/SparkelIcon';
import CommentEditor from './NewComment';
import { useComments } from './useComments';

export interface CommentInterface {
  id: string;
  parentId?: string;
  content: string;
  user: {
    username: string;
  };
  createdAt: string;
  replyingTo?: string;
  projectId: string;
  takeoffId: string | undefined;
  readBy: string[];
  elementIdentifiers: ElementIdentifierDbidType[];
  savedViews: SavedViewDetailsFragment | undefined;
  commentId: string;
  closeParentPopover?: () => void;
  readOnlyMode?: boolean;
  replies?: CommentDeepFragment[];
}

interface TaggedTextProps {
  text: string;
}

function Comment({
  id,
  parentId,
  content,
  user,
  createdAt,
  replyingTo,
  projectId,
  takeoffId,
  readBy,
  elementIdentifiers,
  savedViews,
  commentId,
  closeParentPopover,
  readOnlyMode,
  replies,
}: CommentInterface) {
  const { currentCommentIdx, setCurrentCommentIdx, deleteComment } =
    useComments();
  const [showCommentEditor, toggleCommentEditor] = useState(false);
  const [editorMode, setEditorMode] = useState<'edit' | 'reply'>('reply');
  const AuthUser = useUserTenant().username;

  const [, updateCommentMutation] = useMutation(UpdateCommentMutationDocument);
  const [{ data: projectBreadcrumbData }] = useQuery({
    query: GetProjectNameDocument,
    variables: {
      // This is ok since we pause when this is missing
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      projectId: projectId!,
    },
    pause: !projectId,
  });
  const [{ data: orderBreadcrumbData }] = useQuery({
    query: GetOrderNameDocument,
    variables: {
      // This is ok since we pause when this is missing
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      orderId: takeoffId!,
    },
    pause: !takeoffId,
  });

  const TaggedText: React.FC<TaggedTextProps> = ({ text }) => {
    const pattern = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: JSX.Element[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
      const name: string = match[1];
      const id: string = match[2];
      const nameElement: JSX.Element = (
        <strong key={`name-${id}`}> {name} </strong>
      );

      // Include the non-tagged text before this match
      const nonTaggedText: string = text
        .substring(lastIndex, match.index)
        .trim();
      if (nonTaggedText) {
        parts.push(<span key={`text-${lastIndex}`}>{nonTaggedText}</span>);
      }

      parts.push(nameElement);
      lastIndex = pattern.lastIndex;
    }

    // Include any remaining non-tagged text after the last match
    const remainingText: string = text.substring(lastIndex).trim();
    if (remainingText) {
      parts.push(<span key={`text-${lastIndex}`}>{remainingText}</span>);
    }

    return <div>{parts}</div>;
  };

  const renderCommentEditorByMode = (editorMode: 'edit' | 'reply') => {
    if (editorMode === 'edit' && !parentId)
      return (
        <CommentEditor
          id={id}
          isUpdate={editorMode === 'edit'}
          closeEditor={() => toggleCommentEditor(false)}
          replyingTo={undefined}
          isNewComment={undefined}
          parentId={undefined}
        />
      );
    if (editorMode === 'edit' && parentId)
      return (
        <CommentEditor
          id={id}
          parentId={parentId}
          isUpdate={editorMode === 'edit'}
          closeEditor={() => toggleCommentEditor(false)}
          replyingTo={undefined}
          isNewComment={undefined}
        />
      );
    if (editorMode === 'reply' && !parentId)
      return (
        <CommentEditor
          parentId={id}
          closeEditor={() => toggleCommentEditor(false)}
          replyingTo={user.username}
          isUpdate={undefined}
          isNewComment={undefined}
          id={undefined}
        />
      );
    if (editorMode === 'reply' && parentId)
      return (
        <CommentEditor
          parentId={parentId}
          closeEditor={() => toggleCommentEditor(false)}
          replyingTo={user.username}
          isUpdate={undefined}
          isNewComment={undefined}
          id={undefined}
        />
      );
  };

  const [isHoveringComment, setIsHoveringComment] = useState(false);
  const [isUsingContextMenu, setIsUsingContextMenu] = useState(false);

  const hoverColor = useColorModeValue('gray.50', 'gray.700');
  const readCommentColor = useColorModeValue('gray.600', 'gray.400');

  const _buildActionsDesktopView = () => {
    return (
      window.screen.width > 960 && (
        <Box
          className="commentActions"
          opacity={
            isHoveringComment || isUsingContextMenu || readOnlyMode ? 1 : 0
          }
        >
          {/* Action Button (Rendered Conditionally) */}
          {(AuthUser !== user.username && readOnlyMode) || (
            <Menu
              onOpen={() => setIsUsingContextMenu(true)}
              onClose={() => setIsUsingContextMenu(false)}
              isLazy
            >
              <MenuButton
                as={IconButton}
                size={'xs'}
                variant={'ghost'}
                colorScheme="gray"
                icon={<SparkelIcon icon={faEllipsisH} />}
                onClick={(event) => event.stopPropagation()}
              >
                Actions
              </MenuButton>
              <MenuList>
                {!readOnlyMode && (
                  <MenuItem
                    onClick={toggleReadStatus}
                    icon={
                      <SparkelIcon
                        icon={readBy.includes(AuthUser) ? faCircleDot : faCheck}
                      />
                    }
                  >
                    {readBy.includes(AuthUser)
                      ? 'Mark as unread'
                      : 'Mark as read'}
                  </MenuItem>
                )}
                {AuthUser === user.username && (
                  <MenuItem
                    onClick={(event) => {
                      event.stopPropagation();
                      setCurrentCommentIdx({ id, parentId });
                      setShowDeleteCommentModal(true);
                    }}
                    icon={<SparkelIcon icon={faTrash} color={'red.400'} />}
                    color={'red.400'}
                  >
                    Delete comment
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          )}
        </Box>
      )
    );
  };

  const [showDeleteCommentModal, setShowDeleteCommentModal] = useState(false);

  const navigate = useNavigate();

  const handleCommentClicked = () => {
    {
      orderBreadcrumbData?.order
        ? navigate(
            '/project/' +
              projectId +
              '/takeoff/' +
              takeoffId +
              `?view=${savedViews?.id}` +
              `&commentId=${commentId}`
          )
        : navigate(
            '/project/' +
              projectId +
              '/takeoff' +
              `?view=${savedViews?.id}` +
              `&commentId=${commentId}`
          );

      !readBy.includes(AuthUser) && toggleReadStatus(undefined);
      if (closeParentPopover) {
        closeParentPopover();
      }
    }
  };

  const handleDeleteComment = async () => {
    await deleteComment(currentCommentIdx.id, currentCommentIdx.parentId);

    setShowDeleteCommentModal(false);
  };

  const toggleReadStatus = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined
  ) => {
    event && event.stopPropagation();
    if (readBy.includes(AuthUser)) {
      updateCommentMutation({
        input: {
          id: id,

          patch: {
            readBy: readBy.filter((user) => user !== AuthUser),
          },
        },
      });
    } else {
      updateCommentMutation({
        input: {
          id: id,

          patch: {
            readBy: [...readBy, AuthUser],
          },
        },
      });
    }
  };

  return (
    <>
      <Box
        padding={2}
        paddingTop={1}
        onMouseEnter={() => !isUsingContextMenu && setIsHoveringComment(true)}
        onMouseLeave={() => !isUsingContextMenu && setIsHoveringComment(false)}
        _hover={{
          backgroundColor:
            isUsingContextMenu || readOnlyMode ? undefined : hoverColor,
          cursor: isUsingContextMenu || readOnlyMode ? undefined : 'pointer',
        }}
        borderRadius={'lg'}
        onClick={!readOnlyMode ? handleCommentClicked : undefined}
        width={'100%'}
      >
        <Flex>
          <Box width={'100%'}>
            <HStack alignItems={'start'}>
              <VStack gap={'22px'}>
                <Avatar
                  name={user.username}
                  size="xs"
                  marginTop={1}
                  bg={
                    readBy.includes(AuthUser) && !readOnlyMode
                      ? 'gray.300'
                      : undefined
                  }
                />
                {!readBy.includes(AuthUser) && !readOnlyMode ? (
                  <CircleIcon boxSize={3} color={'red.400'} mt={1} />
                ) : null}
              </VStack>
              <Box width={'100%'}>
                <HStack>
                  {/* User details */}
                  <Flex className="user_details" alignItems={'center'} gap={2}>
                    <Text fontWeight={'bold'} fontSize={'xs'}>
                      {user.username}
                    </Text>
                    {/* Date Posted */}
                    <Text fontSize={'xs'} color={'gray.500'}>
                      {ago(createdAt)}
                    </Text>
                    {/* Comment Actions - Desktop View */}
                  </Flex>
                  <Spacer />
                  {_buildActionsDesktopView()}
                </HStack>
                {!readOnlyMode && projectBreadcrumbData?.project ? (
                  <>
                    {orderBreadcrumbData?.order ? (
                      <Text fontSize="xs" color={'gray.500'}>
                        {projectBreadcrumbData.project.name +
                          ' / ' +
                          orderBreadcrumbData.order.name}
                      </Text>
                    ) : (
                      <Text fontSize="xs" color={'gray.500'}>
                        {projectBreadcrumbData.project.name}
                      </Text>
                    )}
                  </>
                ) : null}
                {/* Comment content */}
                <HStack paddingY={2}>
                  <Text
                    color={
                      readBy.includes(AuthUser) && !readOnlyMode
                        ? readCommentColor
                        : undefined
                    }
                    fontSize={'sm'}
                    fontWeight={
                      readBy.includes(AuthUser) || readOnlyMode
                        ? undefined
                        : 'bold'
                    }
                  >
                    {replyingTo && (
                      <Text
                        as={'span'}
                        fontWeight={'bold'}
                        color="moderate_blue"
                      >
                        @{replyingTo}{' '}
                      </Text>
                    )}
                    <TaggedText text={content} />
                  </Text>
                </HStack>
                {!readBy.includes(AuthUser) && !readOnlyMode ? (
                  <Button
                    colorScheme="gray"
                    size={'xs'}
                    onClick={toggleReadStatus}
                  >
                    Mark as read
                  </Button>
                ) : undefined}
                {replies && replies.length > 0 ? (
                  <Text
                    marginTop={!readBy.includes(AuthUser) ? 3 : 0}
                    fontSize={'sm'}
                    color={'cyan.600'}
                    fontWeight={'medium'}
                  >
                    {replies.length > 1
                      ? `${replies.length} replies`
                      : `${replies.length} reply`}
                  </Text>
                ) : undefined}
              </Box>
            </HStack>
          </Box>
        </Flex>
      </Box>
      <ConfirmDeletionModal
        onConfirm={handleDeleteComment}
        isOpen={showDeleteCommentModal}
        onClose={() => setShowDeleteCommentModal(false)}
      />

      {showCommentEditor && renderCommentEditorByMode(editorMode)}
    </>
  );
}

export default Comment;
